import React, { useRef, useState, useEffect } from 'react';
import './main.css';
import { Helmet } from 'react-helmet';

const Main = () => {
  const navLinksRef = useRef(null);
  const hamburgerRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    issue: '',
    address: '',
    date: '',
    time: ''
  });

  // Hamburger menu logic
  useEffect(() => {
    const hamburger = hamburgerRef.current;
    const navLinks = navLinksRef.current;

    if (!hamburger || !navLinks) return;

    const handleHamburgerClick = () => {
      const isExpanded = hamburger.getAttribute('aria-expanded') === 'true';
      hamburger.setAttribute('aria-expanded', !isExpanded);
      navLinks.classList.toggle('open');
      document.body.classList.toggle('menu-open');
    };

    const handleResize = () => {
      if (window.innerWidth > 768) {
        hamburger.setAttribute('aria-expanded', 'false');
        navLinks.classList.remove('open');
        document.body.classList.remove('menu-open');
      }
    };

    hamburger.addEventListener('click', handleHamburgerClick);
    window.addEventListener('resize', handleResize);

    return () => {
      hamburger.removeEventListener('click', handleHamburgerClick);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // WhatsApp form logic
  const handleSubmit = (e) => {
    e.preventDefault();
    const message =
      '🔧 *Washing Machine Repair Request* 🔧%0A%0A' +
      '👤 *Name:* ' + encodeURIComponent(formData.name) + '%0A' +
      '📱 *Phone:* ' + encodeURIComponent(formData.phone) + '%0A' +
      '⚠️ *Issue:* ' + encodeURIComponent(formData.issue) + '%0A' +
      '📍 *Address:* ' + encodeURIComponent(formData.address) + '%0A' +
      '📅 *Preferred Date:* ' + encodeURIComponent(formData.date) + '%0A' +
      '⏰ *Preferred Time:* ' + encodeURIComponent(formData.time) + '%0A%0A' +
      '🚀 *Request Sent from Friends Traders Website*';
    
    const whatsappNumber = '923317897503';
    window.open('https://wa.me/' + whatsappNumber + '?text=' + message, '_blank');
    
    setFormData({
      name: '',
      phone: '',
      issue: '',
      address: '',
      date: '',
      time: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Stats data
  const stats = [
    { number: "2500+", label: "Machines Repaired", icon: "🔧" },
    { number: "12+", label: "Years Experience", icon: "📅" },
    { number: "98%", label: "Satisfaction Rate", icon: "⭐" },
    { number: "45min", label: "Average Response", icon: "⚡" }
  ];

  // Services data
  const services = [
    { title: "Motor & Drum Repair", icon: "⚙️", description: "Expert repair of washing machine motors and drums for optimal performance." },
    { title: "Water Leakage Fix", icon: "💧", description: "Professional fixing of all types of water leakage issues in washing machines." },
    { title: "Electronic Board Repair", icon: "🔌", description: "Diagnosis and repair of electronic control boards and panels." },
    { title: "Drainage System Repair", icon: "🔄", description: "Clearing blockages and repairing drainage systems effectively." },
    { title: "Spin Cycle Issues", icon: "🌀", description: "Fixing spin cycle problems and balance issues in all models." },
    { title: "Preventive Maintenance", icon: "🛡️", description: "Regular maintenance to prevent future breakdowns and extend lifespan." }
  ];

  // Brands we repair
  const brands = [
    "Samsung", "LG", "Haier", "Dawlance", "Whirlpool", 
    "Kenwood", "Orient", "Panasonic", "Sharp", "TCL"
  ];

  return (
    <>
      <Helmet>
        <title>Premium Washing Machine Repair Service | Friends Traders</title>
        <meta name="description" content="Expert automatic washing machine repair services in Rawalpindi & Islamabad. Fast, reliable, and affordable repairs for all major brands." />
        <meta name="keywords" content="washing machine repair, automatic washing machine service, Rawalpindi, Islamabad, appliance repair" />
        <meta property="og:title" content="Premium Washing Machine Repair | Friends Traders" />
        <meta property="og:description" content="Trusted washing machine repair service with 12+ years experience. Same-day service available." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://friendstraders.com" />
        <meta property="og:image" content="https://friendstraders.com/og-image.jpg" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&family=Inter:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>

      {/* Header */}
      <header className="modern-header">
        <div className="header-container">
          <div className="logo-container">
            <div className="logo-icon">
              <span role="img" aria-label="Washing Machine">🔧</span>
            </div>
            <div className="logo-text">
              <h1>Friends<span>Traders</span></h1>
              <p className="tagline">Premium Appliance Repair</p>
            </div>
          </div>

          <nav className="modern-nav">
            <div className="nav-links" id="nav-links" ref={navLinksRef}>
              <a href="#home" className="nav-link active">Home</a>
              <a href="#about" className="nav-link">About</a>
              <a href="#services" className="nav-link">Services</a>
              <a href="#brands" className="nav-link">Brands</a>
              <a href="#testimonials" className="nav-link">Reviews</a>
              <a href="#contact" className="nav-link">Contact</a>
            </div>
            
            <div className="nav-cta">
              <a href="tel:+923317897503" className="cta-button">
                <span className="phone-icon">📞</span>
                Call Now
              </a>
            </div>

            <button 
              className="hamburger-menu" 
              ref={hamburgerRef}
              aria-label="Toggle navigation menu"
              aria-expanded="false"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="modern-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <span className="hero-badge">Trusted Since 2010</span>
            <h2 className="hero-title">
              Expert <span className="highlight">Washing Machine</span> Repair Services
            </h2>
            <p className="hero-description">
              Fast, reliable, and professional repair services for all automatic washing machine brands. 
              Same-day service with 100% satisfaction guarantee.
            </p>
            <div className="hero-actions">
              <a href="#contact" className="btn-primary">
                <span>📅</span> Book Free Inspection
              </a>
              <a href="tel:+923317897503" className="btn-secondary">
                <span>📞</span> Emergency Call
              </a>
            </div>
            <div className="hero-features">
              <div className="feature">
                <span>⚡</span> Same-Day Service
              </div>
              <div className="feature">
                <span>🏆</span> Certified Technicians
              </div>
              <div className="feature">
                <span>💯</span> Satisfaction Guarantee
              </div>
              <div className="feature">
                <span>💰</span> Affordable Rates
              </div>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
              alt="Professional washing machine repair service"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div className="stat-card" key={index}>
                <div className="stat-icon">{stat.icon}</div>
                <h3 className="stat-number">{stat.number}</h3>
                <p className="stat-label">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">About Us</span>
            <h2 className="section-title">Why Choose <span className="highlight">Friends Traders</span></h2>
            <p className="section-description">
              With over 12 years of experience, we're Rawalpindi's most trusted washing machine repair service.
            </p>
          </div>
          
          <div className="about-content">
            <div className="about-image">
              <img 
                src="https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Our professional repair team"
                loading="lazy"
              />
            </div>
            <div className="about-text">
              <h3>Your Trusted Appliance Repair Partner</h3>
              <p>
                Since 2010, Friends Traders has been the go-to solution for washing machine repairs 
                in Rawalpindi and Islamabad. Our certified technicians combine expertise with cutting-edge 
                technology to deliver unparalleled service quality.
              </p>
              
              <div className="advantages">
                <div className="advantage">
                  <div className="advantage-icon">✅</div>
                  <div className="advantage-content">
                    <h4>Same-Day Service</h4>
                    <p>Most repairs completed within 24 hours</p>
                  </div>
                </div>
                <div className="advantage">
                  <div className="advantage-icon">✅</div>
                  <div className="advantage-content">
                    <h4>Genuine Parts</h4>
                    <p>We use only original and high-quality spare parts</p>
                  </div>
                </div>
                <div className="advantage">
                  <div className="advantage-icon">✅</div>
                  <div className="advantage-content">
                    <h4>Warranty Included</h4>
                    <p>90-day warranty on all repairs and parts</p>
                  </div>
                </div>
                <div className="advantage">
                  <div className="advantage-icon">✅</div>
                  <div className="advantage-content">
                    <h4>Transparent Pricing</h4>
                    <p>No hidden charges, upfront pricing</p>
                  </div>
                </div>
              </div>
              
              <a href="#contact" className="btn-outline">
                <span>📋</span> Get Free Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Our Services</span>
            <h2 className="section-title">Comprehensive <span className="highlight">Repair Solutions</span></h2>
            <p className="section-description">
              We handle all types of washing machine issues with expert precision.
            </p>
          </div>
          
          <div className="services-grid">
            {services.map((service, index) => (
              <div className="service-card" key={index}>
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <a href="#contact" className="service-link">
                  Book Service <span>→</span>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section id="brands" className="brands-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Brands We <span className="highlight">Repair</span></h2>
            <p className="section-description">
              We service all major washing machine brands available in Pakistan.
            </p>
          </div>
          
          <div className="brands-grid">
            {brands.map((brand, index) => (
              <div className="brand-card" key={index}>
                <div className="brand-logo">{brand.charAt(0)}</div>
                <h4>{brand}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">How It Works</span>
            <h2 className="section-title">Simple <span className="highlight">Repair Process</span></h2>
          </div>
          
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3>Book Service</h3>
                <p>Contact us via call, WhatsApp, or online form</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3>Free Inspection</h3>
                <p>Our technician visits for diagnosis and quote</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3>Professional Repair</h3>
                <p>Expert repair using genuine parts</p>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3>Quality Check</h3>
                <p>Thorough testing and warranty provided</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">Customer Reviews</span>
            <h2 className="section-title">What Our <span className="highlight">Customers Say</span></h2>
          </div>
          
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="stars">⭐⭐⭐⭐⭐</div>
                <p>"Excellent service! My washing machine was repaired within 2 hours. Highly recommended!"</p>
              </div>
              <div className="testimonial-author">
                <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Customer" />
                <div>
                  <h4>Ahmed Raza</h4>
                  <p>Rawalpindi</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="stars">⭐⭐⭐⭐⭐</div>
                <p>"Professional team, fair pricing. Will definitely use their services again."</p>
              </div>
              <div className="testimonial-author">
                <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="Customer" />
                <div>
                  <h4>Fatima Khan</h4>
                  <p>Islamabad</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="testimonial-content">
                <div className="stars">⭐⭐⭐⭐⭐</div>
                <p>"Quick response and quality work. Saved me from buying a new machine!"</p>
              </div>
              <div className="testimonial-author">
                <img src="https://randomuser.me/api/portraits/men/65.jpg" alt="Customer" />
                <div>
                  <h4>Bilal Ahmed</h4>
                  <p>Rawalpindi</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <div className="section-header">
                <span className="section-subtitle">Get In Touch</span>
                <h2 className="section-title">Book Your <span className="highlight">Repair Service</span></h2>
                <p className="section-description">
                  Fill the form and we'll contact you within 30 minutes.
                </p>
              </div>
              
              <div className="contact-details">
                <div className="contact-item">
                  <div className="contact-icon">📍</div>
                  <div>
                    <h4>Our Location</h4>
                    <p>Near Waris Khan Metro Station, Rawalpindi</p>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">📞</div>
                  <div>
                    <h4>Call Us</h4>
                    <p><a href="tel:+923317897503">+92 331 7897503</a></p>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">⏰</div>
                  <div>
                    <h4>Working Hours</h4>
                    <p>9:00 AM - 9:00 PM (7 Days a Week)</p>
                  </div>
                </div>
              </div>
              
              <div className="social-links">
                <a href="https://wa.me/+923317897503" className="social-link whatsapp">
                  WhatsApp
                </a>
                <a href="https://facebook.com" className="social-link facebook">
                  Facebook
                </a>
                <a href="tel:+923317897503" className="social-link call">
                  Call Now
                </a>
              </div>
            </div>
            
            <div className="contact-form-container">
              <form onSubmit={handleSubmit} className="modern-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="03XX-XXXXXXX"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="issue">Machine Issue *</label>
                  <select
                    id="issue"
                    name="issue"
                    value={formData.issue}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select issue type</option>
                    <option value="Not Turning On">Not Turning On</option>
                    <option value="Water Leakage">Water Leakage</option>
                    <option value="Not Spinning">Not Spinning</option>
                    <option value="Making Noise">Making Noise</option>
                    <option value="Not Draining">Not Draining</option>
                    <option value="Other">Other Issue</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">Address *</label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Your complete address"
                    rows="3"
                    required
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="date">Preferred Date *</label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="time">Preferred Time *</label>
                    <input
                      type="time"
                      id="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <button type="submit" className="submit-btn">
                  <span>🔧</span> Book Repair Service
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <span className="section-subtitle">FAQ</span>
            <h2 className="section-title">Frequently Asked <span className="highlight">Questions</span></h2>
          </div>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How quickly can you repair my washing machine?</h3>
              <p>Most repairs are completed within 24 hours. Emergency same-day service is available for urgent cases.</p>
            </div>
            <div className="faq-item">
              <h3>Do you provide warranty on repairs?</h3>
              <p>Yes, we provide a 90-day warranty on all repairs and parts used.</p>
            </div>
            <div className="faq-item">
              <h3>What areas do you serve?</h3>
              <p>We cover all areas of Rawalpindi and Islamabad, with service available in surrounding regions.</p>
            </div>
            <div className="faq-item">
              <h3>Are your technicians certified?</h3>
              <p>All our technicians are certified professionals with extensive training in appliance repair.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <div className="map-wrapper">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13248.453605485918!2d73.0477662!3d33.6840622!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38df95e951af7adb%3A0x573067e256df96f1!2sFriend%20Traders!5e0!3m2!1sen!2s!4v1698765432100!5m2!1sen!2s"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Friends Traders Location"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="modern-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <div className="logo-icon">🔧</div>
                <div>
                  <h3>Friends<span>Traders</span></h3>
                  <p>Premium Appliance Repair</p>
                </div>
              </div>
              <p className="footer-about">
                Trusted washing machine repair service in Rawalpindi and Islamabad since 2010.
              </p>
            </div>
            
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul className="footer-links">
                <li><a href="#home">Home</a></li>
                <li><a href="#services">Services</a></li>
                <li><a href="#about">About Us</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4>Our Services</h4>
              <ul className="footer-links">
                <li><a href="#services">Motor Repair</a></li>
                <li><a href="#services">Water Leakage Fix</a></li>
                <li><a href="#services">Electronic Repair</a></li>
                <li><a href="#services">Maintenance</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4>Contact Info</h4>
              <ul className="footer-contact">
                <li>📍 Near Waris Khan Metro Station</li>
                <li>📞 <a href="tel:+923317897503">+92 331 7897503</a></li>
                <li>⏰ 9:00 AM - 9:00 PM Daily</li>
              </ul>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} Friends Traders. All rights reserved.</p>
            <div className="footer-legal">
              <a href="#privacy">Privacy Policy</a>
              <a href="#terms">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a 
        href="https://wa.me/+923317897503" 
        className="whatsapp-float"
        target="_blank" 
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
      >
        <span className="whatsapp-icon">💬</span>
        <span className="whatsapp-text">Chat Now</span>
      </a>

      {/* Emergency Call Button */}
      <a 
        href="tel:+923317897503" 
        className="call-float"
        aria-label="Emergency Call"
      >
        <span className="call-icon">📞</span>
        <span className="call-text">Emergency</span>
      </a>
    </>
  );
};

export default Main;